import { useMemo, useState } from "react";
import { SearchX, RotateCcw } from "lucide-react";
import Seo from "@/components/Seo";
import Reveal from "@/components/Reveal";
import PropertyCard from "@/components/PropertyCard";
import { useSite } from "@/context/SiteContext";

const selectCls =
  "w-full appearance-none bg-white border border-slate-200 rounded-xl px-4 py-2.5 text-sm font-medium text-slate-700 focus:outline-none focus:ring-2 focus:ring-brand/40 focus:border-brand transition";

export default function Imoveis() {
  const { properties, loadingProps } = useSite();
  const [tipo, setTipo] = useState("todos");
  const [cidade, setCidade] = useState("todas");
  const [bairro, setBairro] = useState("todos");
  const [faixa, setFaixa] = useState("todas");
  const [quartos, setQuartos] = useState("todos");
  const [ordem, setOrdem] = useState("recentes");

  const cidades = useMemo(() => [...new Set(properties.map((p) => p.city).filter(Boolean))].sort(), [properties]);
  const bairros = useMemo(
    () =>
      [...new Set(properties.filter((p) => cidade === "todas" || p.city === cidade).map((p) => p.neighborhood).filter(Boolean))].sort(),
    [properties, cidade]
  );

  const filtered = useMemo(() => {
    let list = [...properties];
    if (tipo !== "todos") list = list.filter((p) => p.type === tipo);
    if (cidade !== "todas") list = list.filter((p) => p.city === cidade);
    if (bairro !== "todos") list = list.filter((p) => p.neighborhood === bairro);
    if (faixa !== "todas") {
      const [min, max] = faixa.split("-").map(Number);
      list = list.filter((p) => p.price != null && p.price >= min && (max ? p.price <= max : true));
    }
    if (quartos !== "todos") list = list.filter((p) => p.bedrooms >= Number(quartos));
    if (ordem === "menor-preco") list.sort((a, b) => (a.price ?? Infinity) - (b.price ?? Infinity));
    else if (ordem === "maior-preco") list.sort((a, b) => (b.price ?? 0) - (a.price ?? 0));
    else list.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
    return list;
  }, [properties, tipo, cidade, bairro, faixa, quartos, ordem]);

  const reset = () => {
    setTipo("todos");
    setCidade("todas");
    setBairro("todos");
    setFaixa("todas");
    setQuartos("todos");
    setOrdem("recentes");
  };

  return (
    <div className="pt-[72px]" data-testid="catalog-page">
      <Seo
        title="Imóveis à Venda | Casa Vale Engenharia e Construção"
        description="Catálogo de casas e apartamentos prontos para morar da Casa Vale. Filtre por tipo, cidade, bairro, preço e dormitórios e encontre seu novo lar."
      />
      <section className="bg-slate-900 py-16 md:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-limebrand text-xs font-bold uppercase tracking-[0.3em] mb-3">Catálogo</p>
          <h1 className="font-serif-display text-4xl sm:text-5xl text-white tracking-tight" data-testid="catalog-title">
            Imóveis à venda
          </h1>
          <p className="mt-4 text-slate-400 max-w-xl">Casas e apartamentos prontos, construídos pela Casa Vale.</p>
        </div>
      </section>

      <section className="sticky top-[72px] z-30 bg-white/90 backdrop-blur-md border-b border-slate-200 py-4" data-testid="filter-bar">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
            <select value={tipo} onChange={(e) => setTipo(e.target.value)} className={selectCls} aria-label="Tipo de imóvel" data-testid="filter-tipo">
              <option value="todos">Tipo: Todos</option>
              <option value="casa">Casa</option>
              <option value="apartamento">Apartamento</option>
            </select>
            <select
              value={cidade}
              onChange={(e) => { setCidade(e.target.value); setBairro("todos"); }}
              className={selectCls}
              aria-label="Cidade"
              data-testid="filter-cidade"
            >
              <option value="todas">Cidade: Todas</option>
              {cidades.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
            <select value={bairro} onChange={(e) => setBairro(e.target.value)} className={selectCls} aria-label="Bairro" data-testid="filter-bairro">
              <option value="todos">Bairro: Todos</option>
              {bairros.map((b) => <option key={b} value={b}>{b}</option>)}
            </select>
            <select value={faixa} onChange={(e) => setFaixa(e.target.value)} className={selectCls} aria-label="Faixa de preço" data-testid="filter-preco">
              <option value="todas">Preço: Todos</option>
              <option value="0-1000000">Até R$ 1 mi</option>
              <option value="1000000-2000000">R$ 1 mi — R$ 2 mi</option>
              <option value="2000000-4000000">R$ 2 mi — R$ 4 mi</option>
              <option value="4000000-0">Acima de R$ 4 mi</option>
            </select>
            <select value={quartos} onChange={(e) => setQuartos(e.target.value)} className={selectCls} aria-label="Dormitórios" data-testid="filter-quartos">
              <option value="todos">Quartos: Todos</option>
              <option value="1">1+</option>
              <option value="2">2+</option>
              <option value="3">3+</option>
              <option value="4">4+</option>
            </select>
            <select value={ordem} onChange={(e) => setOrdem(e.target.value)} className={selectCls} aria-label="Ordenação" data-testid="filter-ordem">
              <option value="recentes">Mais recentes</option>
              <option value="menor-preco">Menor preço</option>
              <option value="maior-preco">Maior preço</option>
            </select>
          </div>
        </div>
      </section>

      <section className="py-12 md:py-16 bg-[#FAFAFA] min-h-[50vh]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-sm text-slate-500 mb-8" data-testid="results-counter">
            Exibindo <span className="font-bold text-slate-900">{filtered.length}</span>{" "}
            {filtered.length === 1 ? "imóvel encontrado" : "imóveis encontrados"}
          </p>
          {loadingProps ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
              {[0, 1, 2].map((i) => (
                <div key={i} className="rounded-2xl bg-slate-100 aspect-[3/4] animate-pulse" />
              ))}
            </div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-20" data-testid="empty-state">
              <SearchX className="w-10 h-10 text-slate-300 mx-auto mb-4" />
              <h2 className="text-lg font-bold text-slate-800">Nenhum imóvel encontrado</h2>
              <p className="text-sm text-slate-500 mt-2">Ajuste os filtros para ampliar sua busca.</p>
              <button
                type="button"
                onClick={reset}
                className="mt-6 inline-flex items-center gap-2 bg-brand hover:bg-brand-dark text-white text-sm font-bold px-6 py-2.5 rounded-full transition-colors"
                data-testid="reset-filters-btn"
              >
                <RotateCcw className="w-4 h-4" />
                Limpar filtros
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
              {filtered.map((p, i) => (
                <Reveal key={p.id} delay={Math.min(i, 5) * 0.08}>
                  <PropertyCard property={p} index={i} />
                </Reveal>
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
